import catalog from '../content/navigator-tools/navigator-catalog.json';
import packages from '../content/navigator-tools/navigator-packages.json';
import { navigatorPackagePath } from './paths';

function enrichPackage(item) {
  if (!item) return null;
  return {
    ...item,
    purpose: item.description,
    resource_type: item.kind === 'tool' ? 'Tool' : 'Center',
    embedHref: item.html || '',
    detailPath: navigatorPackagePath(item),
  };
}

function listedPackages() {
  return (packages || []).filter((item) => item.listed !== false);
}

export function getNavigatorCatalog() {
  return catalog;
}

export function getNavigatorLibraryStats() {
  const items = listedPackages();
  return {
    total: items.length,
    centers: items.filter((item) => item.kind !== 'tool').length,
    tools: items.filter((item) => item.kind === 'tool').length,
    journeys: (catalog.journeys || []).length,
    listed: items.length,
  };
}

export function listNavigatorJourneys() {
  return catalog.journeys || [];
}

export function getNavigatorJourney(id) {
  return (catalog.journeys || []).find((journey) => journey.id === id) || null;
}

export function listNavigatorRoles() {
  return catalog.roles || [];
}

export function listNavigatorObjectives() {
  return catalog.objectives || [];
}

export function getNavigatorPackage(id) {
  const item = listedPackages().find((entry) => entry.id === id);
  return enrichPackage(item || null);
}

export function getNavigatorPackageBySlug(slug) {
  const item = listedPackages().find((entry) => entry.slug === slug);
  return enrichPackage(item || null);
}

export function getRelatedNavigatorPackages(item) {
  if (!item?.relatedSlugs?.length) return [];
  return item.relatedSlugs
    .map((relatedSlug) => listedPackages().find((entry) => entry.slug === relatedSlug))
    .filter(Boolean)
    .map(enrichPackage);
}

export function getSiblingNavigatorPackages(item, limit = 4) {
  if (!item) return [];
  return listedPackages()
    .filter(
      (entry) => entry.id !== item.id && entry.category === item.category
    )
    .slice(0, limit)
    .map(enrichPackage);
}

const DOWNLOAD_EXT = /\.(xlsx|xls|pdf|docx|doc|csv|png|svg|jpg|jpeg|zip)$/i;

export function resolveNavigatorRouteFromHref(href) {
  if (!href) return null;
  let url;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return null;
  }

  if (DOWNLOAD_EXT.test(url.pathname)) return null;
  if (url.origin !== window.location.origin) return null;

  const match = url.pathname.match(/\/kc-packages\/([^/]+)\//);
  if (!match) return null;

  const directory = decodeURIComponent(match[1]);
  const item = listedPackages().find((entry) => entry.directory === directory);
  if (!item) return null;
  return navigatorPackagePath(item);
}

function matchesResourceBrowse(item, resource) {
  if (!resource) return true;
  if (resource === 'Center' || resource === 'navigator') {
    return item.kind !== 'tool';
  }
  if (resource === 'Tool' || resource === 'tool') {
    return item.kind === 'tool';
  }
  const haystack = `${item.title} ${item.description}`.toLowerCase();
  if (resource === 'templates') {
    return item.kind === 'tool' && /template|charter|inventory|reporting|playbook/.test(haystack);
  }
  if (resource === 'calculators') {
    return /calculator/.test(haystack);
  }
  if (resource === 'assessments') {
    return /assessment|scorecard|worksheet|matrix|checklist|questionnaire/.test(
      haystack
    );
  }
  return true;
}

function matchesType(item, type) {
  if (!type) return true;
  if (type === 'Tool' || type === 'tool') return item.kind === 'tool';
  if (type === 'Center' || type === 'navigator') return item.kind !== 'tool';
  return true;
}

export function listNavigatorPackages(filters = {}) {
  const {
    q = '',
    type = '',
    category = '',
    stage = '',
    role = '',
    journey = '',
    resource = '',
  } = filters;

  const query = String(q || '')
    .toLowerCase()
    .trim();
  const journeyDef = journey ? getNavigatorJourney(journey) : null;
  const journeyIds = journeyDef ? new Set(journeyDef.packageIds) : null;

  return listedPackages()
    .filter((item) => {
      if (!matchesType(item, type)) return false;
      if (category && item.category !== category) return false;
      if (stage && item.stage !== stage) return false;
      if (role && !(item.roleIds || []).includes(role)) return false;
      if (journeyIds && !journeyIds.has(item.id)) return false;
      if (!matchesResourceBrowse(item, resource)) return false;
      if (!query) return true;
      const haystack =
        `${item.title} ${item.description} ${item.category} ${item.id} ${item.stage} ${item.slug}`.toLowerCase();
      return haystack.includes(query);
    })
    .map(enrichPackage);
}
